/**
 * Message statuses (Postgres enum msg_status) and the rules for moving between them. The worker uses
 * these rules to apply provider reports safely (a late or replayed webhook can never move a delivered
 * text back to sent); the web app uses the labels.
 */

export const MESSAGE_STATUSES = [
  'queued',
  'held',
  'sending',
  'sent',
  'delivered',
  'failed',
  'blocked',
  'unknown',
  'received',
  'cancelled',
] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

/** Statuses that never change again (also enforced by the messages trigger). */
export const FINAL_STATUSES: readonly MessageStatus[] = [
  'delivered',
  'failed',
  'blocked',
  'received',
  'cancelled',
];

/** What a provider status report can say about one of our texts. */
export type ProviderReport = 'sent' | 'delivered' | 'failed';

/**
 * Whether a provider report may move a message from `current` to `report`. Reports only move forward:
 * sending/unknown → sent/delivered/failed, sent → delivered/failed. Anything else (a replay, an
 * out-of-order `message.sent` after `message.finalized`, a report for a text we never handed over) is
 * ignored.
 */
export function canApplyProviderReport(current: MessageStatus, report: ProviderReport): boolean {
  if (current === 'sending' || current === 'unknown') return true;
  if (current === 'sent') return report === 'delivered' || report === 'failed';
  return false;
}

/** True when the provider has (or may have) accepted the text: counts as one send for the number. */
export function isAccepted(status: MessageStatus): boolean {
  return status === 'sent' || status === 'delivered';
}

export type StatusTone = 'neutral' | 'progress' | 'ok' | 'warning' | 'error';

export interface StatusLabel {
  label: string;
  tone: StatusTone;
}

const LABELS: Readonly<Record<MessageStatus, StatusLabel>> = {
  queued: { label: 'Queued', tone: 'neutral' },
  held: { label: 'Held', tone: 'warning' },
  sending: { label: 'Sending', tone: 'progress' },
  sent: { label: 'Sent', tone: 'ok' },
  delivered: { label: 'Delivered', tone: 'ok' },
  failed: { label: 'Failed', tone: 'error' },
  blocked: { label: 'Not sent', tone: 'error' },
  unknown: { label: 'Delivery unknown', tone: 'warning' },
  received: { label: 'Received', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export function statusLabel(status: MessageStatus): StatusLabel {
  return LABELS[status];
}

/** Reasons the worker stores in messages.status_reason, explained for people. */
const REASONS: Readonly<Record<string, string>> = {
  archived: 'The merchant is archived.',
  opted_out: 'The merchant opted out of texts.',
  do_not_contact: 'The merchant is marked do not contact.',
  carrier_block:
    'The recipient opted out at their carrier. Only they can lift it, by texting START.',
  landline: 'This number is a landline and cannot receive texts.',
  not_sms_capable: 'This number is a landline (or cannot receive texts), so texting it is now off.',
  too_long: 'The text is longer than 10 segments.',
  empty: 'The text is empty.',
  no_number: 'No sending number is available. An admin needs to add one in Settings → Numbers.',
  number_released: "The merchant's sending number was released.",
  number_paused: "The merchant's sending number is paused.",
  number_provider_mismatch: "The merchant's sending number cannot be used by the current provider.",
  sending_disabled: 'Texting is not enabled for this workspace yet (10DLC approval).',
  daily_cap:
    'The sending number reached its daily limit; this text goes out after midnight Pacific.',
  carrier_opt_out: 'The recipient blocked texts at their carrier.',
  not_registered: 'The sending number is not registered for texting (10DLC).',
  invalid_destination: 'The carrier says this number cannot be texted.',
  invalid_source: 'The sending number cannot send right now.',
  spam_blocked: 'The carrier blocked this text as spam.',
  account_limit: 'The messaging account reached its spending limit.',
  rejected: 'The provider refused this text.',
  delivery_failed: 'The carrier could not deliver this text.',
  retries_exhausted: 'The provider stayed unavailable after 6 attempts.',
  ambiguous: 'The provider did not confirm whether it accepted this text.',
  replied: 'Cancelled because the merchant replied.',
  cancelled: 'Cancelled before it went out.',
  // M7: the sending window, the merchant's location and the per-24-hour caps.
  quiet_hours: "It's outside sending hours for the merchant. It goes out when their window opens.",
  holiday:
    "It's a federal holiday for the merchant. Campaign and sequence texts go out on the next sending day.",
  no_window:
    'The sending rules leave no time to send in the next two weeks. Check Settings → Sending hours.',
  timezone_unknown: "We can't tell the merchant's time zone. Add their state and it goes out.",
  outside_us: "This number is outside the US. Fundline can't text it.",
  state_cap:
    'The limit of texts per 24 hours for this merchant is reached. It goes out once an earlier text is 24 hours old.',
  rules_unavailable: "The sending rules couldn't be loaded, so texts wait. We've been alerted.",
  compliance_check_failed:
    "The sending rules couldn't be checked, so this text waits. We've been alerted.",
  // M9: campaigns.
  suppressed: 'This number is on the suppression list.',
  brand_daily_cap:
    "Your workspace reached today's texting limit; this text goes out after midnight Pacific.",
  campaign_paused: 'The campaign is paused. It goes out when the campaign resumes.',
  campaign_cancelled: 'Not sent: the campaign was cancelled.',
  expired: 'Not sent: the campaign ran out of sending days.',
  campaign_test: 'A test of a campaign.',
};

/** A sentence for a stored reason ("delivery_failed:40008" → the delivery_failed sentence). */
export function describeReason(reason: string | null | undefined): string | null {
  if (!reason) return null;
  const base = reason.split(':')[0] ?? reason;
  return REASONS[base] ?? reason;
}
